import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

const GoogleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4"/>
    <path d="M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.24 19.252C9.07376 19.252 6.39287 17.1399 5.44288 14.3003H1.45801V17.3912C3.43223 21.4434 7.54226 24.0008 12.24 24.0008Z" fill="#34A853"/>
    <path d="M5.44277 14.3003C5.21387 13.5681 5.08188 12.7862 5.08188 12.0004C5.08188 11.2146 5.21387 10.4327 5.44277 9.70051V6.60962H1.45791C0.530724 8.24284 0 10.0682 0 12.0004C0 13.9326 0.530724 15.7579 1.45791 17.3912L5.44277 14.3003Z" fill="#FBBC05"/>
    <path d="M12.24 4.74966C14.0291 4.74966 15.6265 5.36715 16.8582 6.54066L20.2717 3.12721C18.2003 1.18941 15.4708 0 12.24 0C7.54226 0 3.43223 2.55737 1.45801 6.60962L5.44288 9.70051C6.39287 6.86095 9.07376 4.74966 12.24 4.74966Z" fill="#EA4335"/>
  </svg>
);

export default function InviteFormComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { regToken } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState({});
  const [formData, setFormData] = useState({
    fullName: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const googleInitializedRef = useRef(false);
  const scriptLoadAttemptedRef = useRef(false);
  const mountedRef = useRef(false);
  const fetchAttemptedRef = useRef(false);

  // Add image loading check
  const [logoLoaded, setLogoLoaded] = useState(false);
  
  useEffect(() => {
    // If we don't have invite details or no logo, consider logo as loaded
    if (!inviteDetails || !inviteDetails.companyLogo) {
      setLogoLoaded(true);
      return;
    }
    
    // If we have a logo URL, preload it
    const img = new Image();
    img.onload = () => setLogoLoaded(true);
    img.onerror = () => setLogoLoaded(true); // Consider as loaded even on error
    img.src = inviteDetails.companyLogo;
    
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setLogoLoaded(true);
    }, 3000); // 3 seconds max wait time
    
    return () => clearTimeout(timeout);
  }, [inviteDetails]);

  useEffect(() => {
    // Only run once on mount
    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;
    setInitialLoading(true);

    // Check if we have a valid registration token
    if (!regToken) {
      // If no regToken, redirect to regular signup
      navigate('/signup');
      return;
    }

    // Save the registration token in localStorage for later use
    localStorage.setItem('inviteRegToken', regToken);

    // First check the registration state
    fetch(`${API_BASE}/signup/${regToken}/check`)
      .then(response => {
        if (!response.ok) {
          if (response.status === 404) {
            sessionStorage.removeItem('registrationToken');
            localStorage.removeItem('inviteRegToken');
            navigate('/signup');
            return null;
          }
          
          if (response.status === 401) {
            localStorage.clear();
            sessionStorage.removeItem('registrationToken');
            navigate('/login', { replace: true });
            return null;
          }
          
          throw new Error('Failed to check registration state');
        }
        return response.json();
      })
      .then(stateData => {
        if (!stateData) return null; // Already handled redirection

        console.log("Registration state:", stateData);
        
        // If we're not supposed to be on the invite form, redirect
        if (stateData.state !== 'InviteForm') {
          if (stateData.state === 'Login') {
            navigate('/login', { replace: true });
          } else {
            navigate(`/signup/${regToken}`, { replace: true });
          }
          return null;
        }
        
        // Continue with invite details
        return fetch(`${API_BASE}/signup/inviteDetails?regToken=${regToken}`);
      })
      .then(response => {
        if (!response) return; // Already handled redirection
        
        console.log('Invite details response:', response);
        if (response.ok) {
          return response.json();
        }
        throw new Error('Failed to fetch invite details');
      })
      .then(data => {
        if (!data) return; // Already handled redirection
        
        console.log('Invite details data:', data);
        setInviteDetails(data);
        // Initialize Google Sign-In if isGmail is true
        if (data.isGmail) {
          console.log('Initializing Google Sign-In');
          initializeGoogleSignIn();
        }
        setInitialLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch invite details:', err);
        setInitialLoading(false);
      });
      
    // Safety timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      setInitialLoading(false);
    }, 5000); // 5 seconds max wait time
    
    return () => clearTimeout(loadingTimeout);
  }, [regToken, navigate, location.search]);

  // Add debug log for render
  console.log('Current inviteDetails:', inviteDetails);

  const initializeGoogleSignIn = () => {
    // Skip if script loading was already attempted
    if (scriptLoadAttemptedRef.current) return;
    scriptLoadAttemptedRef.current = true;
    
    // Clear any existing Google state and script on page load
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
    
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create fresh script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (googleInitializedRef.current) return;
      console.log('Google script loaded successfully');
      
      if (window.google) {
        try {
          // Clear any existing configurations
          window.google.accounts.id.cancel();
          
          // Wait a brief moment before initializing
          setTimeout(() => {
            window.google.accounts.id.initialize({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              callback: handleGoogleResponse,
              auto_select: false,
              cancel_on_tap_outside: true,
              prompt_parent_id: 'google_btn_container',
              context: 'signin',
              itp_support: true,
              use_fedcm_for_prompt: true,
              state_cookie_domain: window.location.hostname,
              allowed_parent_origin: window.location.origin
            });
            
            googleInitializedRef.current = true;
            setGoogleScriptLoaded(true);
            console.log('Google authentication initialized');
          }, 100);
        } catch (error) {
          console.error('Error initializing Google authentication:', error);
        }
      } else {
        console.error('Google API not available after script load');
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google authentication script');
    };
    document.body.appendChild(script);

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script) {
        script.remove();
      }
    };
  };

  const handleGoogleResponse = async (response) => {
    if (!response.credential) {
      setError({ general: 'Google login failed. Please try again.' });
      return;
    }

    // Decode the JWT to check email
    const base64Url = response.credential.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    const googleEmail = payload.email;

    // Check if the Google email matches the invited email
    if (inviteDetails && inviteDetails.email && googleEmail.toLowerCase() !== inviteDetails.email.toLowerCase()) {
      setError({ general: `Please use the Google account associated with ${inviteDetails.email}. You attempted to sign in with ${googleEmail}.` });
      return;
    }

    setGoogleLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('key');

    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          credential: response.credential,
          key: key,
          regToken: regToken
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Check for phone verification data with exact property names
        if (data.phoneCountry && data.phone && data.regToken) {
          // Store phone information for SMS verification
          const phoneVerificationData = {
            PhoneCountry: data.phoneCountry,
            Phone: data.phone,
            regToken: data.regToken
          };
          
          sessionStorage.setItem('phoneVerificationData', JSON.stringify(phoneVerificationData));
          navigate('/sms-verification');
          return;
        }

        // Check for registration token first
        if (data.regToken) {
          sessionStorage.setItem('registrationToken', data.regToken);
          navigate(`/signup/${data.regToken}`, { replace: true });
          return;
        }
        
        if (data.jwtToken) {
          // Store the JWT token and navigate to home
          sessionStorage.setItem('jwt', data.jwtToken);
          navigate('/home', { replace: true });
        } else if (data.token) {
          // Backward compatibility for old token field
          sessionStorage.setItem('jwt', data.token);
          navigate('/home', { replace: true });
        } else if (data.redirectUrl) {
          // Extract token from redirectUrl
          const registrationToken = data.redirectUrl.split('/').pop();
          
          // Save token for future use
          sessionStorage.setItem('registrationToken', registrationToken);
          
          // Check registration state to determine next step
          fetch(`${API_BASE}/signup/${registrationToken}/check`)
            .then(response => {
              if (!response.ok) {
                throw new Error('Failed to check registration state');
              }
              return response.json();
            })
            .then(stateData => {
              console.log("Registration state after Google sign-in:", stateData);
              
              // Navigate based on registration state
              switch(stateData.state) {
                case 'InviteForm':
                  navigate(`/signup/${registrationToken}/invite`, { replace: true });
                  break;
                case 'MailVerification':
                  navigate(`/signup/${registrationToken}`, { replace: true });
                  break;
                case 'PhoneVerification':
                  navigate(`/signup/${registrationToken}`, { replace: true });
                  break;
                case 'Login':
                  navigate('/login', { replace: true });
                  break;
                default:
                  navigate(`/signup/${registrationToken}`, { replace: true });
              }
            })
            .catch(err => {
              console.error('Failed to check registration state:', err);
              // Fall back to basic redirect
              navigate(`/signup/${registrationToken}`, { replace: true });
            });
        } else {
          setError({ general: 'Invalid response from server' });
        }
      } else {
        setError({ general: data.message || 'Google login failed. Please try again.' });
      }
    } catch (err) {
      setError({ general: 'Unable to connect to the server. Please try again.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleClick = () => {
    console.log('Google button clicked');
    if (!window.google?.accounts?.id) {
      console.error('Google Sign-In not initialized');
      return;
    }

    try {
      console.log('Attempting Google Sign-In');
      // Cancel any existing prompt first
      window.google.accounts.id.cancel();

      // Configure One Tap and standard sign-in
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin'
      });
      
      // Create a hidden button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.display = 'none';
      document.body.appendChild(buttonContainer);

      // Render the button (it will be hidden)
      window.google.accounts.id.renderButton(
        buttonContainer,
        { type: 'standard' }
      );

      // Function to try the fallback method
      const tryFallbackMethod = () => {
        console.log('Executing fallback method');
        const button = buttonContainer.querySelector('div[role="button"]');
        if (button) {
          button.click();
        }
      };

      let oneTapTriggered = false;

      // Set a timeout to try fallback if One Tap doesn't respond
      const timeoutId = setTimeout(() => {
        if (!oneTapTriggered) {
          console.log('One Tap timeout - no response received');
          tryFallbackMethod();
        }
      }, 1000);

      // Try One Tap first
      console.log('Trying One Tap');
      window.google.accounts.id.prompt((notification) => {
        oneTapTriggered = true;
        clearTimeout(timeoutId);
        
        console.log('One Tap callback received:', notification);
        
        if (!notification) {
          console.log('No notification, trying fallback');
          tryFallbackMethod();
          return;
        }

        // Only try fallback for specific cases
        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          console.log('One Tap not displayed. Reason:', reason);
          tryFallbackMethod();
        } else if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason();
          console.log('One Tap skipped. Reason:', reason);
          if (reason === 'user_cancel') {
            // User explicitly cancelled, don't show fallback
            console.log('User cancelled One Tap');
          } else {
            tryFallbackMethod();
          }
        } else if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          console.log('One Tap dismissed. Reason:', reason);
          if (reason === 'credential_returned') {
            // Successful sign-in, don't do anything
            console.log('Successful sign-in via One Tap');
          } else if (reason === 'flow_restarted') {
            // Flow is being restarted, don't do anything
            console.log('One Tap flow restarted');
          } else {
            tryFallbackMethod();
          }
        }
        // Don't handle unknown states - let One Tap continue
      });

      // Cleanup function
      const cleanup = () => {
        if (document.body.contains(buttonContainer)) {
          document.body.removeChild(buttonContainer);
        }
      };

      // Clean up after 5 seconds
      setTimeout(cleanup, 5000);

    } catch (error) {
      console.error('Error in Google Sign-In:', error);
      setError({ general: 'Failed to show sign-in prompt. Please try again.' });
    }
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.fullName || formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Please enter your full name (minimum 2 characters)';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6 || !/^[\x20-\x7E]+$/.test(formData.password)) {
      newErrors.password = 'Use 6 or more characters with a mix of letters, numbers & symbols';
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(prev => ({ ...prev, [name]: '', general: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate()) return;

    setLoading(true);
    setError({});

    try {
      const searchParams = new URLSearchParams(location.search);
      const key = searchParams.get('key');

      // Store key in sessionStorage in case we need it later
      if (key) {
        sessionStorage.setItem('inviteKey', key);
      }

      const response = await fetch(`${API_BASE}/signup/${regToken}/invite`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName.trim(),
          password: formData.password,
          key: key || sessionStorage.getItem('inviteKey')
        })
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Registration not found
          sessionStorage.removeItem('registrationToken');
          localStorage.removeItem('inviteRegToken');
          sessionStorage.removeItem('inviteKey');
          navigate('/signup');
          return;
        }

        if (response.status === 401) {
          // Unauthorized
          localStorage.clear();
          sessionStorage.removeItem('registrationToken');
          sessionStorage.removeItem('inviteKey');
          localStorage.removeItem('inviteRegToken');
          navigate('/login', { replace: true });
          return;
        }

        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update name');
      }

      // After successful name update, continue with email verification
      const savedKey = key || sessionStorage.getItem('inviteKey');
      navigate(`/signup/${regToken}?from=email&key=${savedKey}`);
    } catch (err) {
      setError({ general: err.message || 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Show loading screen while fetching invite details or loading logo
  if (initialLoading || (inviteDetails?.companyLogo && !logoLoaded)) {
    return <LoadingScreen />;
  }

  return (
    <div className="bg-[var(--card-bg)] rounded-lg p-6 border border-[var(--border-color)]">
      <div className="text-center">
        {inviteDetails?.companyLogo && (
          <div className="flex justify-center mb-4">
            <img 
              src={inviteDetails.companyLogo} 
              alt={`${inviteDetails.companyName} logo`}
              className="h-16 w-auto"
            />
          </div>
        )}
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">Welcome!</h2>
        {inviteDetails && inviteDetails.companyName && (
          <p className="mt-2 mb-4 text-[var(--text-secondary)]">
            You've been invited to join <span className="text-lg font-bold text-[var(--text-primary)]">{inviteDetails.companyName}</span>
          </p>
        )}
      </div>

      {error.general && !error.general.includes("Please use the Google account") && (
        <div className="text-red-500 text-center">{error.general}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="relative">
            <input
              type="text"
              name="fullName"
              id="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Full Name"
              disabled={loading}
              className={`w-full p-2 pl-10 pr-9 border rounded bg-[var(--card-bg)] focus:outline-none ${
                error.fullName 
                  ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                  : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
              } disabled:opacity-50`}
            />
            <User className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
          {error.fullName && (
            <p className="mt-1 text-red-500 text-sm">{error.fullName}</p>
          )}
        </div>

        <div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              disabled={loading}
              className={`w-full p-2 pl-10 pr-9 border rounded bg-[var(--card-bg)] focus:outline-none ${
                error.password 
                  ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                  : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
              } disabled:opacity-50`}
            />
            <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-2 flex items-center"
              tabIndex={-1}
              disabled={loading}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5 text-[var(--text-primary)]" />
              ) : (
                <Eye className="w-5 h-5 text-[var(--text-primary)]" />
              )}
            </button>
          </div>
          {error.password && (
            <p className="mt-1 text-red-500 text-sm">{error.password}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center items-center px-4 py-2 bg-[var(--accent-primary)] text-white font-medium rounded hover:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] disabled:opacity-50 transition-colors duration-200"
        >
          {loading ? 'Setting up...' : 'Continue'}
        </button>

        {inviteDetails && inviteDetails.isGmail && (
          <>
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--border-color)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-[var(--card-bg)] text-[var(--text-secondary)]">Or</span>
              </div>
            </div>

            <div id="google_btn_container">
              <button
                type="button"
                onClick={handleGoogleClick}
                disabled={loading || googleLoading || !googleScriptLoaded}
                className="w-full flex justify-center items-center gap-2 px-4 py-2 font-medium border border-[var(--border-color)] rounded bg-[var(--card-bg)] hover:bg-[var(--hover-light)] active:bg-[var(--hover-light)] focus-visible:bg-[var(--hover-light)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50"
              >
                <GoogleIcon />
                <span className="text-[var(--text-primary)]">Continue with Google</span>
              </button>
            </div>
            {error.general && error.general.includes("Please use the Google account") && (
              <p className="mt-2 text-xs text-red-500">{error.general}</p>
            )}
          </>
        )}
      </form>
    </div>
  );
} 